import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { Link } from 'react-router';

import { CodigoDePieza } from '@/components/CodigoDePieza';
import { enPesos, precioParaMostrar, tieneDescuentoPorBulto } from '@/lib/catalogo';
import type { Producto } from '@/tipos';

/**
 * Ver una pieza sin perder el lugar en la grilla.
 *
 * Con 2831 productos paginados de a doce, abrir la ficha para descartar un producto cuesta ir y
 * volver, y volver deja la grilla en la página 1. Esto muestra lo que se necesita para decidir
 * —foto, código, descripción, precio y condiciones— sin navegar.
 *
 * No pide nada al servidor: el producto y su foto ya están en la grilla.
 */
export function VistaRapida({
  producto,
  foto,
  conPrecios,
  onCerrar,
}: {
  producto: Producto | null;
  foto: string | null;
  conPrecios: boolean;
  onCerrar: () => void;
}) {
  // Sin producto no hay nada que mostrar: el diálogo está cerrado y las dos cuentas dan null.
  const { conOferta } = precioParaMostrar(
    producto ?? { price: null, discount: null, is_no_discount: false },
  );

  return (
    <Dialog
      open={producto !== null}
      onClose={onCerrar}
      maxWidth="md"
      fullWidth
      aria-labelledby="titulo-vista-rapida"
      slotProps={{ paper: { sx: { bgcolor: '#1e1e1e', backgroundImage: 'none' } } }}
    >
      {producto && (
        <DialogContent sx={{ p: 0 }}>
          <IconButton
            aria-label="Cerrar"
            onClick={onCerrar}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
              bgcolor: 'rgba(0,0,0,.45)',
              color: '#fff',
              '&:hover': { bgcolor: 'rgba(0,0,0,.7)' },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
            <Box
              sx={{
                // El mismo blanco que la tarjeta y la ficha: las fotos vienen con fondo blanco.
                bgcolor: '#fff',
                width: { xs: '100%', md: 380 },
                minHeight: { xs: 240, md: 340 },
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
              }}
            >
              {foto ? (
                <Box
                  component="img"
                  src={foto}
                  alt={producto.description}
                  sx={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain' }}
                />
              ) : (
                <Typography variant="caption" sx={{ color: 'grey.500' }}>
                  Imagen no disponible
                </Typography>
              )}
            </Box>

            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1.5, flexGrow: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Chip label={producto.category} color="primary" size="small" />

                {conPrecios && (
                  <Chip
                    label={producto.is_in_stock ? 'Stock Disponible' : 'Sin Stock'}
                    color={producto.is_in_stock ? 'success' : 'error'}
                    size="small"
                  />
                )}
              </Box>

              <Box component="h2" id="titulo-vista-rapida" sx={{ m: 0, fontSize: 'inherit' }}>
                <CodigoDePieza codigo={producto.code} variante="display" />
              </Box>

              <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: '52ch' }}>
                {producto.description}
              </Typography>

              {conPrecios && (
                <>
                  <Divider sx={{ borderColor: 'rgba(255,255,255,.08)', my: 0.5 }} />

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    {producto.price !== null && (
                      <Dato etiqueta="Precio Lista" valor={enPesos(producto.price)} destacado />
                    )}

                    {conOferta !== null && (
                      <Dato etiqueta="Precio Oferta" valor={enPesos(conOferta)} destacado />
                    )}

                    {producto.bulk?.trim() && <Dato etiqueta="Bulto" valor={producto.bulk} />}
                    {producto.cut?.trim() && <Dato etiqueta="Corte" valor={producto.cut} />}

                    <Dato
                      etiqueta="Descuento por Bulto"
                      valor={tieneDescuentoPorBulto(producto.bulk_discount) ? 'Sí' : 'No'}
                    />
                  </Box>
                </>
              )}

              <Button
                component={Link}
                to={`/catalogo/${encodeURIComponent(producto.code)}`}
                variant="contained"
                color="primary"
                sx={{ alignSelf: 'flex-start', mt: 'auto' }}
              >
                Ver ficha completa
              </Button>
            </Box>
          </Box>
        </DialogContent>
      )}
    </Dialog>
  );
}

function Dato({
  etiqueta,
  valor,
  destacado = false,
}: {
  etiqueta: string;
  valor: string;
  destacado?: boolean;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}
      >
        {etiqueta}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: destacado ? 'success.main' : 'text.primary',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {valor}
      </Typography>
    </Box>
  );
}
